import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";
import { useDismissableLayer } from "../DismissableLayer/useDismissableLayer";

const ModalMobile = ({ children, className, isOpen, onClose, showClose }) => {
  const panelRef = useRef(null);

  // ✅ Register as top layer so underlying Popovers/Dropdowns won't dismiss on clicks
  // We keep closeOnOutside=false because you already close on backdrop click.
  useDismissableLayer({
    enabled: isOpen,
    refs: [panelRef],
    onEscape: () => onClose?.(),
    closeOnOutside: false,
  });

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    // Only close when clicking the backdrop itself (not inside the panel)
    if (e.target === e.currentTarget) onClose?.();
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[1500] flex items-end bg-black/40 sm:items-center sm:justify-center sm:bg-[rgba(49,64,86,0.70)] sm:backdrop-blur"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleBackdropClick}
      >
        <motion.div
          ref={panelRef}
          className={`
            relative z-[1501] w-full max-h-screen overflow-y-auto bg-white
            rounded-t-2xl sm:rounded-xl sm:m-6 sm:max-h-[80vh]
            transition-opacity
            ${className || ""}
          `}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ damping: 30, stiffness: 300, type: "spring" }}
          onClick={(e) => e.stopPropagation()}
          // extra safety against "mousedown" click-away listeners elsewhere
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {showClose && (
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="absolute right-4 top-4 rounded-md p-1 text-gray500 hover:bg-gray100"
            >
              <IoClose className="size-6" />
            </button>
          )}

          {children}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default ModalMobile;
